import { ReactElement, ReactNode, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface DeleteConfirmationDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when dialog is closed (cancel button or backdrop click)
   */
  onClose: () => void;
  /**
   * Callback when delete is confirmed
   */
  onConfirm: () => void;
  /**
   * The name of the item being deleted (displayed in bold)
   * Example: "My Account", "Transaction #123", "USD"
   */
  itemName: string;
  /**
   * The type of item being deleted (used in default message)
   * Example: "account", "transaction", "currency"
   * @default "item"
   */
  itemType?: string;
  /**
   * Custom title for the dialog
   * @default "Confirm Delete"
   */
  title?: string;
  /**
   * Custom confirmation message (overrides default message with itemType)
   * If provided, this will be used instead of the default message.
   * Use {itemName} placeholder to include the item name.
   * Example: "Are you sure you want to delete {itemName}?"
   */
  message?: string;
  /**
   * Optional warning message or content to display above the main message
   * Can be a string or ReactNode for more complex layouts (e.g., lists of dependencies)
   */
  warningMessage?: ReactNode;
  /**
   * Whether the delete button should be disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether to disable closing the dialog (X icon, backdrop click, Cancel button)
   * Useful when an operation is in progress (e.g., deleting)
   * @default false
   */
  disableClose?: boolean;
  /**
   * Text for the confirm button
   * @default "Delete"
   */
  confirmButtonText?: string;
  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelButtonText?: string;
  /**
   * Whether to show an Alert component for the warning message
   * If false, warning message is shown as plain Typography
   * @default false
   */
  showWarningAsAlert?: boolean;
  /**
   * Whether to require the user to manually type the item name to confirm deletion
   * If true, a TextField will be shown and the delete button will only be enabled
   * when the typed name matches the itemName exactly
   * @default false
   */
  requireNameConfirmation?: boolean;
  /**
   * Whether to hide the "This action cannot be undone." text at the bottom
   * Useful when the warning message already includes this information
   * @default false
   */
  hideCannotUndoText?: boolean;
}

/**
 * Reusable Delete Confirmation Dialog Component
 *
 * Displays a confirmation dialog for deleting items with customizable messages,
 * warning content, and support for disabled states.
 *
 * Designed to be extensible for future features like secure deletion
 * (requiring manual text input to confirm).
 *
 * @example
 * // Basic usage
 * <DeleteConfirmationDialog
 *   open={deleteDialogOpen}
 *   onClose={() => setDeleteDialogOpen(false)}
 *   onConfirm={handleDeleteConfirm}
 *   itemName={account.accountName}
 *   itemType="account"
 * />
 *
 * @example
 * // With warning message
 * <DeleteConfirmationDialog
 *   open={deleteDialogOpen}
 *   onClose={() => setDeleteDialogOpen(false)}
 *   onConfirm={handleDeleteConfirm}
 *   itemName={currency.code}
 *   itemType="currency"
 *   warningMessage={
 *     <Box>
 *       <Typography variant="body2" sx={{ mb: 0.5 }}>
 *         This currency is used in:
 *       </Typography>
 *       {accounts.map((acc) => (
 *         <Typography key={acc.id} variant="caption" display="block">
 *           • {acc.accountName}
 *         </Typography>
 *       ))}
 *     </Box>
 *   }
 *   disabled={isCurrencyInUse}
 * />
 *
 * @example
 * // With custom message
 * <DeleteConfirmationDialog
 *   open={deleteDialogOpen}
 *   onClose={() => setDeleteDialogOpen(false)}
 *   onConfirm={handleDeleteConfirm}
 *   itemName="Workspace"
 *   message="Deleting this workspace will permanently remove all data. Are you sure?"
 *   confirmButtonText="Delete Workspace"
 * />
 */
function DeleteConfirmationDialog({
  open,
  onClose,
  onConfirm,
  itemName,
  itemType = "item",
  title = "Confirm Delete",
  message,
  warningMessage,
  disabled = false,
  confirmButtonText = "Delete",
  cancelButtonText = "Cancel",
  showWarningAsAlert = false,
  requireNameConfirmation = false,
  hideCannotUndoText = false,
  disableClose = false,
}: DeleteConfirmationDialogProps): ReactElement {
  const [typedName, setTypedName] = useState("");

  // Reset typed name when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setTypedName("");
    }
  }, [open]);

  // Handle close - only close if not disabled
  const handleClose = (event?: object, reason?: string): void => {
    if (disableClose) {
      // Prevent closing when disabled (ignore backdrop click and escape key)
      return;
    }
    onClose();
  };

  // Check if the typed name matches the item name (case-sensitive)
  const isNameMatch = requireNameConfirmation ? typedName.trim() === itemName.trim() : true;

  // Format the message - handle {itemName} placeholder if present, or use default
  const formattedMessage = message ? (
    message.includes("{itemName}") ? (
      message.split("{itemName}").map((part, index) => {
        if (index === 0) return <span key={index}>{part}</span>;
        return (
          <span key={index}>
            <strong>{itemName}</strong>
            {part}
          </span>
        );
      })
    ) : (
      message
    )
  ) : (
    <>
      Are you sure you want to delete the {itemType} <strong>{itemName}</strong>?
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      disableEscapeKeyDown={disableClose}
      PaperProps={{
        sx: { 
          borderRadius: 1,
          width: "400px",
          maxWidth: "90vw",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", position: "relative" }}>
        {title}
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={disableClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {warningMessage && (
          <Box sx={{ mb: 2 }}>
            {showWarningAsAlert ? (
              <Alert severity="warning" sx={{ mt: 0 }}>
                {typeof warningMessage === "string" ? (
                  <Typography variant="body2">{warningMessage}</Typography>
                ) : (
                  warningMessage
                )}
              </Alert>
            ) : (
              typeof warningMessage === "string" ? (
                <Typography variant="body2" color="text.secondary">
                  {warningMessage}
                </Typography>
              ) : (
                warningMessage
              )
            )}
          </Box>
        )}
        <Typography sx={{ textAlign: "center" }}>
          {formattedMessage}
        </Typography>
        
        {requireNameConfirmation && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              To confirm, please type <strong>{itemName}</strong> in the field below:
            </Typography>
            <TextField
              fullWidth
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type the name to confirm"
              autoFocus
              error={typedName.trim() !== "" && !isNameMatch}
              helperText={
                typedName.trim() !== "" && !isNameMatch
                  ? "The name does not match"
                  : ""
              }
            />
          </Box>
        )}

        {!hideCannotUndoText && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              textAlign: "center", 
              mt: requireNameConfirmation ? 1 : 3, 
              mb: 1 
            }}
          >
            This action cannot be undone.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          disabled={disableClose}
          sx={{ flex: 1, py: 1.5 }}
        >
          {cancelButtonText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={disabled || (requireNameConfirmation && !isNameMatch)}
          sx={{ flex: 1, py: 1.5 }}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteConfirmationDialog;
